import AwesomePhoneNumber from "awesome-phonenumber";
import formatCurrency from "format-currency";
import { cloneDeep, filter, get, isEmpty, map } from "lodash-es";
import {
  extend,
  ValidationProvider,
  ValidationObserver,
  setInteractionMode,
} from "vee-validate";
import {
  required,
  email,
  max,
  min,
  min_value as minValue,
  double,
  regex,
  alpha_num,
  alpha_spaces as alphaSpaces,
  alpha_dash as alphaDash,
} from "vee-validate/dist/rules";
import Vue from "vue";

import { isEmailValid } from "@/plugins/helpers/__ts/formatting/formatting.isEmail.plugin.ts";

// validation observer & validation provider
Vue.component("validation-provider", ValidationProvider);
Vue.component("validation-observer", ValidationObserver);

// interaction mode for input[type="hidden"]
setInteractionMode("inputOnly", () => ({
  on: ["input"],
}));

// rules
extend("required", {
  ...required,
  message: "{_field_} is required.",
});

extend("regex", {
  ...regex,
  message: "Format {_field_} does not match",
});

extend("alpha_num", {
  ...alpha_num,
  message: "{_field_} may only contain alphabetic and number.",
});

extend("requiredSelect", {
  ...required,
  validate(value) {
    let result = false;
    if (value && Object.keys(value).length > 0) {
      result = true;
    } else {
      result = false;
    }
    return result;
  },
  message: "{_field_} is required.",
});

extend("percentage", {
  ...double,
  message: "{_field_} must be upto 2 decimal places or only number.",
  // message: '{_field_} may not be greater than 2 decimal places or only number. E.g: 5, 1.5, 1.75',
  validate(value) {
    // return {
    //     valid: double.validate(value) || /^[0-9]*$/.test(value)
    // };

    // regex for a percentage (decimal upto 2 decimal places)
    const regexDecimal = /^\d+\.\d{0,2}$/;
    const resultDecimal = regexDecimal.test(value);

    // regex for a percentage (number)
    const regexNumber = /^[0-9]*$/;
    const resultNumber = regexNumber.test(value);

    // regex for prevent e.g: 0.2
    const regexDecimal2 = /^\d*\.?\d+$/;
    const resultDecimal2 = regexDecimal2.test(value);

    if ((resultDecimal || resultNumber) && resultDecimal2) {
      return true;
    }
    return false;
  },
});

extend("requiredNumber", {
  ...required,
  validate(value) {
    const xregex = /^[^0]/;
    return xregex.test(value);
  },
  message: "{_field_} is required.",
});

extend("password", {
  ...required,
  validate(value) {
    const regexObj = {
      numberRegex: /[0-9]/,
      uppercaseRegex: /[A-Z]/,
      lowercaseRegex: /[a-z]/,
      specialcharRegex: /[`!@#$%^&*()_+\-={};':"|,.<>?~]/,
      minmaxRegex: /^.{8,16}$/,
    };
    let isValidPassword = true;
    for (const prop in regexObj) {
      isValidPassword = isValidPassword && regexObj[prop].test(value);
    }
    return isValidPassword;
  },
  message:
    "Make sure your password contains a minimum of a number, a capital letter, a lowercase letter, a symbol and length between 8 - 16 characters",
});

extend("requiredCPrice", {
  validate(value) {
    return {
      required: true,
      valid:
        get(value, "cost", null) !== null &&
        get(value, "cost", null) !== "" &&
        typeof get(value, "cost", null) !== "undefined",
    };
  },
  message: "{_field_} is required",
  computesRequired: true,
});

extend("requiredCPrices", {
  ...required,
  validate(value) {
    const regexExceptZero = /^[0]/;
    const resultExceptZero = regexExceptZero.test(value.cost);

    const regexEmptyValue = /^ *$/;
    const resultEmptyValue = regexEmptyValue.test(value.cost);

    // empty
    if (resultEmptyValue) {
      return false;
    }
    // not empty and zero is avail
    if (!resultEmptyValue && resultExceptZero) {
      return false;
    }
    // not empty and zero is not avail
    return true;

    // return !resultEmptyValue;
  },
  message: "{_field_} is required and dont put 0 at the beginning.",
});

extend("exceptZeroAtBegin", {
  ...required,
  validate(value) {
    const regexExceptZero = /^[0]/;
    const resultExceptZero = regexExceptZero.test(value.cost);
    return !resultExceptZero;
  },
  message: "{_field_} is required and dont put 0 at the beginning.",
});

extend("requiredCUploadForm", {
  validate(value) {
    return {
      required: true,
      valid:
        value &&
        value.files !== null &&
        typeof value.files !== "undefined" &&
        value.files.length,
    };
  },
  message: "{_field_} is required",
  computesRequired: true,
});

extend("requiredExcludeFalsey", {
  validate(value) {
    return {
      required: true,
      valid:
        ["", null].indexOf(value) === -1 &&
        !isEmpty(value) &&
        typeof value !== "undefined",
    };
  },
  message: "{_field_} is required",
  computesRequired: true,
});

extend("email", {
  ...email,
  message: "{_field_} must be an e-mail.",
});

extend("multipleEmail", {
  // validation multiple email:
  // https://stackoverflow.com/questions/55057875/validating-an-email-address-inside-a-chip
  validate(value) {
    for (let i = 0; i < value.length; i++) {
      // eslint-disable-next-line
      if (!isEmailValid(value[i])) {
        return false;
      }
    }
    return true;
  },
  message: `{_field_} must be an e-mail, example: john.due@gmail.com.`,
});

extend("uniqueEmail", {
  params: ["target"],
  validate(value, { target }) {
    const results = [];

    // filtered array based on target.subject
    const filteredArray = target.object.filter(
      (item) => item.name !== target.subject,
    );
    filteredArray.forEach((item, index) => {
      // find index based on target.subject
      const resultIndex = target.object.findIndex(
        (object) => object.name === item.name,
      );
      // find matching values in two arrays
      // https://stackoverflow.com/questions/12433604/how-can-i-find-matching-values-in-two-arrays
      const result = target.object[resultIndex].value.filter((element) =>
        value.includes(element),
      );
      results.push({
        id: index,
        name: item.name,
        value: !!result.length > 0,
      });
    });
    const filtered = results.filter((item) => item.value);
    if (!target.subject && filtered.length === 0) {
      return true;
    }
    if (!target.subject && filtered.length > 0) {
      return true;
    }
    if (target.subject && filtered.length === 0) {
      return true;
    }
    if (target.subject && filtered.length > 0) {
      return false;
    }
    return false;
  },
  message: `{_field_} must be an e-mail, example: john.due@gmail.com.`,
});

extend("confirmPassword", {
  params: ["target"],
  validate(value, { target }) {
    return value === target;
  },
  message: "Password confirmation does not match",
});

extend("nameChar", {
  validate(value) {
    const xregex = /^[A-Za-z0-9-\s.']*$/i;
    return xregex.test(value);
  },
  message: `{_field_} may contain only alphabet, number, space, period, dash, and single quote.`,
});

extend("nameVietnamChar", {
  validate(value) {
    const xregex =
      /^[a-zA-ZÀÁÂÃÈÉÊẾÌÍÒÓÔÕÙÚĂĐĨŨƠàáâãèéêếìíòóôõùúăđĩũơƯĂẠẢẤẦẨẪẬẮẰẲẴẶẸẺẼỀỀỂưăạảấầẩẫậắằẳẵặẹẻẽềềểỄỆỈỊỌỎỐỒỔỖỘỚỜỞỠỢỤỦỨỪễệỉịọỏốồổỗộớờởỡợụủứừỬỮỰỲỴÝỶỸửữựỳỵỷỹ ]*$/i;
    return xregex.test(value);
  },
  message: `{_field_} may contain only alphabet, number, space, period, dash, and single quote.`,
});

extend("fullNameValidation", {
  validate(value) {
    const xregex = /^[A-Za-z0-9\s.'|-]*$/i;
    return xregex.test(value);
  },
  message: `{_field_} may contain only alphabet, number, space, period, |, dash, and single quote.`,
});

extend("phoneType", {
  validate(value) {
    const xregex = /^[-A-Za-z0-9\s.'()/]*$/i;
    return xregex.test(value);
  },
  message: `{_field_} may contain only alphabet, number, space, period, and single quote.`,
});

extend("leadingPhoneNumber", {
  validate(value, { startDigit }) {
    const regex = new RegExp(`^${startDigit}[0-9]*$`);
    return regex.test(value);
  },
  params: ["startDigit"],
  message: `{_field_} must must start with {startDigit} number.`,
});

extend("nameUser", {
  validate(value) {
    const xregex = /^[\u0E00-\u0E7FA-Za-z\s.']*$/i;
    return xregex.test(value);
  },
  message: `{_field_} may contain only alphabet, space, period, and single quote.`,
});

extend("cantBeZero", {
  validate(value) {
    let field = value;
    // if <c-price />
    if (get(value, "cost", null) !== null) {
      field = value.cost;
    }
    return field !== 0 && field !== "0";
  },
  message: `{_field_} must be greater than zero.`,
});

extend("phone", {
  params: ["isPlusRequired"],
  // email or phone:
  // https://stackoverflow.com/questions/50033651/how-can-vee-validate-validates-email-and-phone-at-the-same-time
  getMessage(field) {
    return `${field} must be a valid phone number`;
  },
  validate(value, { isPlusRequired }) {
    // regex for a phone number
    let mobileRegex = /^\+?[0-9]{7,15}$/;
    if (isPlusRequired) {
      mobileRegex = /^\+[0-9]{7,15}$/;
    }

    // Check for either of these to return true
    return mobileRegex.test(value);
  },
});

extend("requiredPhone", {
  getMessage(field) {
    return `${field} must be a valid phone number`;
  },
  validate(value) {
    if (value.number) {
      return true;
    }
    return false;
  },
});

extend("requiredCPhone", {
  ...required,
  validate(value) {
    const regexExceptZero = /^[0]/;
    const resultExceptZero = regexExceptZero.test(value.number);

    const regexEmptyValue = /^ *$/;
    const resultEmptyValue = regexEmptyValue.test(value.number);

    // empty
    if (resultEmptyValue) {
      return false;
    }
    // not empty and zero is avail
    if (!resultEmptyValue && resultExceptZero) {
      return false;
    }
    return true;
  },
  message: "{_field_} is required and dont put 0 at the beginning.",
});

extend("requiredUploaderCombobox", {
  params: ["label"],
  validate: (value) => {
    // find invalid field --> doesn't have documents
    const invalidFields = filter(
      value,
      (fieldValue) => !(get(fieldValue, "documents", []) || []).length,
    );
    return !invalidFields.length;
  },
  message: (field, placeholders) => {
    // find invalid field --> doesn't have documents
    const invalidFields = filter(
      get(placeholders, "_value_", []) || [],
      (fieldValue) => !(get(fieldValue, "documents", []) || []).length,
    );
    const getLabels = map(invalidFields, (invalidField) => {
      // use label or text (based on attached from DB or dynamic user's free text)
      let result = invalidField.text;
      if (invalidField.label) {
        result = invalidField.label;
      }

      if (invalidFields.length > 1) {
        return `<li>${result}</li>`;
      }

      return result;
    }).join("");

    if (invalidFields.length > 1) {
      return `${placeholders.label} needs these documents to be uploaded: <ul>${getLabels}</ul>`;
    }

    return `${placeholders.label} needs <b>${getLabels}</b> to be uploaded.`;
  },
});

extend("requiredUploader", {
  ...required,
  params: ["label"],
  message: (field, placeholders) => `${placeholders.label} is required.`,
});

extend("phoneNumber", {
  getMessage(field) {
    return `${field} must be a valid phone number`;
  },
  params: ["dialCode"],
  validate(value, { dialCode }) {
    let phoneWithDialCode = `+${dialCode}${value}`;
    if (dialCode === "") {
      // validation is called outside <c-phone />
      phoneWithDialCode = `+${value.country.dialCode}${value.number}`;
    }

    // checking validation phone number (exist or not exist)
    const phoneNumber = new AwesomePhoneNumber(phoneWithDialCode).isValid();

    // regex for a phone number
    // except zero at beginning of value
    const mobileRegex = /^[0]/;
    const mobileRegexResult = !mobileRegex.test(value);

    // Check for either of these to return true
    if (mobileRegexResult && phoneNumber) {
      return true;
    }
    return false;
  },
});

extend("phoneNumbers", {
  getMessage(field) {
    return `${field} must be a valid phone number`;
  },
  validate(value) {
    // validation for empty value.number (not required)
    if (!value.number) {
      return true;
    }

    const phoneWithDialCode = `+${value.country.dialCode}${value.number}`;

    // checking validation phone number (exist or not exist)
    const phoneNumber = new AwesomePhoneNumber(phoneWithDialCode).isValid();

    // regex for a phone number
    // except zero at beginning of value
    const regexExceptZero = /^[0]/;
    const regexExceptZeroResult = !regexExceptZero.test(value.number);

    // Check for either of these to return true
    if (regexExceptZeroResult && phoneNumber) {
      return true;
    }
    return false;
  },
});

extend("numberOnly", {
  // email or phone:
  // https://stackoverflow.com/questions/50033651/how-can-vee-validate-validates-email-and-phone-at-the-same-time
  validate(value) {
    // regex for a phone number
    const regexNumeric = /^[0-9]*$/;

    // Check for either of these to return true
    return regexNumeric.test(value);
  },
  message: `{_field_} must be number only.`,
});

extend("icon", {
  validate(value) {
    // regex for a icon (alphabet lowercase and dash)
    const regexIcon = /^[a-z0-9-]*$/i;
    // Check for either of these to return true
    return regexIcon.test(value);
  },
  message: `{_field_} is may contain only alphabet lowercase, number and dash.`,
});

extend("max", {
  ...max,
  params: ["num"],
  validate(value, { num }) {
    let valueToCheck = value;
    if (typeof value.number !== "undefined") {
      valueToCheck = value.number;
    }

    const x = parseInt(num, 10);
    return valueToCheck.length <= x;
  },
  message: `{_field_} may not be greater than {num} char.`,
});

extend("minChar", {
  ...min,
  params: ["num"],
  validate(value, { num }) {
    let valueToCheck = value;
    if (typeof value.number !== "undefined") {
      valueToCheck = value.number;
    }

    const x = parseInt(num, 10);
    return valueToCheck.length >= x;
  },
  message: `{_field_} should be greater than {num} char.`,
});

extend("lessThan", {
  params: ["num"],
  validate(value, { num }) {
    return parseFloat(value) < parseFloat(num);
  },
  message: `{_field_} may be less than {num}.`,
});

extend("minValue", minValue);

extend("maxNumber", {
  params: ["num"],
  validate(value, { num }) {
    return parseFloat(value) <= parseFloat(num);
  },
  message: `{_field_} may not be greater than {num}.`,
});

extend("maxPrice", {
  ...max,
  params: ["maxValue"],
  validate(value, { maxValue }) {
    // for CPriceGeneral
    let val = value;
    if (typeof value.cost !== "undefined" && value.cost !== null) {
      val = value.cost;
    }

    // convert value to price format
    if (typeof val === "number") {
      val = `${val}`;
    }
    const valueWithoutDots = val.split(".").join("");
    const valueFormatted = parseInt(valueWithoutDots, 10);

    // convert maxValue to price format
    let maxValueString = maxValue;
    if (typeof maxValue === "number") {
      maxValueString = `${maxValue}`;
    }
    const maxValueWithoutDots = maxValueString.split(".").join("");
    const maxValueFormatted = parseInt(maxValueWithoutDots, 10);

    if (valueFormatted > maxValueFormatted) {
      return false;
    }
    return true;
  },
  message: `{_field_} may not be greater than {maxValue}.`,
});

extend("minPrice", {
  ...max,
  params: ["minimumValue"],
  validate(value, { minimumValue }) {
    // for CPriceGeneral
    let val = value;
    if (typeof value.cost !== "undefined" && value.cost !== null) {
      val = value.cost;
    }

    // convert value to price format
    if (typeof val === "number") {
      val = `${val}`;
    }
    const valueWithoutDots = val.split(".").join("");
    const valueFormatted = parseInt(valueWithoutDots, 10);

    // convert minimumValue to price format
    let minimumValueString = minimumValue;
    if (typeof minimumValue === "number") {
      minimumValueString = `${minimumValue}`;
    }
    const minimumValueWithoutDots = minimumValueString.split(".").join("");
    const minimumValueFormatted = parseInt(minimumValueWithoutDots, 10);

    if (valueFormatted < minimumValueFormatted) {
      return false;
    }
    return true;
  },
  message: `{_field_} may not be smaller than {minimumValue}.`,
});

extend("maxPrices", {
  ...max,
  params: ["maxValue"],
  validate(value, { maxValue }) {
    let val = value;

    if (typeof value.cost !== "undefined" && value.cost !== null) {
      [val] = value.cost;
    }

    // convert value to price format
    const valueWithoutDots = val ? val.split(".").join("") : null;
    const valueFormatted = parseInt(valueWithoutDots, 10);

    // convert maxValue to price format
    const maxValueWithoutDots = maxValue.split(".").join("");
    const maxValueFormatted = parseInt(maxValueWithoutDots, 10);

    if (valueFormatted > maxValueFormatted) {
      return false;
    }
    return true;
  },
  message: `{_field_} may not be greater than {maxValue}.`,
});

extend("min", {
  ...min,
  params: ["num"],
  validate(value, { num }) {
    const x = parseInt(num, 10);
    return value.length >= x;
  },
  message: `{_field_} should be greater than {num} char.`,
});

extend("min_value", {
  ...minValue,
  message: "{_field_} must be {min} or more",
});

extend("alpha_num_dash_spaces", {
  validate(value) {
    const xregex = /^[0-9A-Z-\s]*$/i;
    return xregex.test(value);
  },
  message: `{_field_} may only alphabetic, numbers, dash or spaces.`,
});

extend("alpha_num_dash_spaces_unicode", {
  validate(value) {
    const xregex = /^[\u0E00-\u0E7F0-9A-Z-\s]*$/i;
    return xregex.test(value);
  },
  message: `{_field_} may only alphabetic, numbers, dash or spaces.`,
});

extend("alpha_num_dash_spaces_slash", {
  validate(value) {
    const xregex = /^[0-9A-Z-\s/]*$/i;
    return xregex.test(value);
  },
  message: `{_field_} may only alphabetic, numbers, dash or spaces.`,
});

extend("descriptionChar", {
  validate(value) {
    const xregex = /^[0-9A-Z-\s',.()]*$/i;
    return xregex.test(value);
  },
  message: `{_field_} may contain only alphabet, number, dash, space, period, quote, or comma.`,
});

extend("alpha_spaces", {
  ...alphaSpaces,
  message: `{_field_} may only contain alphabetic char.`,
});

extend("alpha_dash", {
  ...alphaDash,
  message: `{_field_} may only contain alphabetic, numbers, dashes or underscores.`,
});

extend("alpha_dash_slash_underscore", {
  validate(value) {
    const xregex = /^[0-9A-Z-/_]*$/i;
    return xregex.test(value);
  },
  message: `{_field_} may only alphabetic, numbers, dash, underscore or slash.`,
});

extend("alpha_dash_slash_underscore_period_space_parentheses_colon", {
  validate(value) {
    const xregex = /^[0-9A-Z-/_.\s():]*$/i;
    return xregex.test(value);
  },
  message: `{_field_} may only alphabetic, numbers, dash, underscore, slash, period, space, parentheses or colon.`,
});

extend("availability_value", {
  params: ["val"],
  validate(value, { val }) {
    return val === "true";
  },
  message: `{_field_} {_value_} already available on the system.`,
});

extend("isValidBankAccountNumber", {
  params: ["val"],
  validate(value, { val }) {
    return val === "true";
  },
  message: "{_field_} is invalid",
});

extend("isValidUrl", {
  validate(value) {
    const regexUrl =
      /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g;
    return regexUrl.test(value);
  },
  message: "{_field_} should be a valid url",
});

// --- special validation for <c-item-counter-price />

// name: ""
// totalItem: 2
// totalPrice: Object
//  cost: "917.167"
// `currency: Object

// - required: 'Name, counter, and item price is required'
function icpRequired(value) {
  const name =
    get(value, "name", null) !== null ? cloneDeep(get(value, "name", "")) : "";
  const totalItem =
    get(value, "totalItem", null) !== null
      ? cloneDeep(get(value, "totalItem", ""))
      : "";
  const totalPriceCost =
    get(value, "totalPrice.cost", null) !== null
      ? cloneDeep(get(value, "totalPrice.cost", ""))
      : "";
  return name !== "" && totalItem !== "" && totalPriceCost !== "";
}

// - min: 'Counter and price should be greater than min'
function icpIsPassMin(value, minItem, pricePerItem) {
  const totalItem =
    get(value, "totalItem", null) !== null
      ? cloneDeep(get(value, "totalItem", ""))
      : "";
  const totalPriceCost =
    get(value, "totalPrice.cost", null) !== null
      ? cloneDeep(get(value, "totalPrice.cost", ""))
      : "";
  const priceChecker =
    typeof totalPriceCost !== "number"
      ? parseInt(totalPriceCost.replace(/\D/g, ""), 10)
      : totalPriceCost;
  return (
    // totalItem pass min
    totalItem !== "" &&
    totalItem >= minItem &&
    // totalPriceCost pass min
    totalPriceCost !== "" &&
    priceChecker >= minItem * pricePerItem
  );
}

// - max: 'Counter shouldn't be greater than maxItem and price shouldn't be greater than maxItem * pricePerItem'
function icpIsPassMax(value, maxItem, pricePerItem) {
  const totalItem =
    get(value, "totalItem", null) !== null
      ? cloneDeep(get(value, "totalItem", ""))
      : "";
  const totalPriceCost =
    get(value, "totalPrice.cost", null) !== null
      ? cloneDeep(get(value, "totalPrice.cost", ""))
      : "";
  const priceChecker =
    typeof totalPriceCost !== "number"
      ? cloneDeep(parseInt(totalPriceCost.replace(/\D/g, ""), 10))
      : totalPriceCost;
  return (
    // totalItem pass min
    totalItem !== "" &&
    totalItem <= maxItem &&
    // totalPriceCost pass min
    totalPriceCost !== "" &&
    priceChecker <= maxItem * pricePerItem
  );
}

// - numberOnly: totalItem and totalPrice should be a number
function icpNumberOnly(value) {
  const regexNumeric = /^[0-9]*$/;

  const totalItem =
    get(value, "totalItem", null) !== null
      ? cloneDeep(get(value, "totalItem", ""))
      : "";
  const totalPriceCost =
    get(value, "totalPrice.cost", null) !== null
      ? cloneDeep(get(value, "totalPrice.cost", ""))
      : "";
  const priceChecker =
    typeof totalPriceCost !== "number"
      ? cloneDeep(parseInt(totalPriceCost.replace(/\D/g, ""), 10))
      : totalPriceCost;

  // Check for either of these to return true
  return regexNumeric.test(totalItem) && regexNumeric.test(priceChecker);
}

function icpPriceNotZero(value) {
  const totalPriceCost =
    get(value, "totalPrice.cost", null) !== null
      ? cloneDeep(get(value, "totalPrice.cost", ""))
      : "";
  return totalPriceCost !== "0" && totalPriceCost !== 0;
}

// - nameChar: Name (nameChar validation)
// function icpNameChar(value) {
//     const name = get(value, 'name', null) !== null ? get(value, 'name', '') : '';
//     const xregex = /^[A-Za-z0-9\s.']*$/i;
//     return xregex.test(name);
// }

extend("itemCounterPriceValidation", {
  params: ["minItem", "maxItem", "pricePerItem"],
  validate(value, { minItem, maxItem, pricePerItem }) {
    return (
      icpRequired(value) &&
      icpIsPassMin(value, minItem, pricePerItem) &&
      icpIsPassMax(value, maxItem, pricePerItem) &&
      icpNumberOnly(value) &&
      icpPriceNotZero(value)
      // && icpNameChar(value)
    );
  },
  message: (field, placeholders) => {
    if (!icpRequired(placeholders._value_)) {
      return "Name, counter, and item price is required";
    }

    if (
      !icpIsPassMin(
        placeholders._value_,
        placeholders.minItem,
        placeholders.pricePerItem,
      )
    ) {
      const minTotalRange = formatCurrency(
        placeholders.minItem * placeholders.pricePerItem,
        {
          symbol: "",
          format: "%s%v",
          locale: "id",
          nanZero: true,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        },
      );
      const currency =
        get(placeholders._value_, "totalPrice.currency.name", null) !== null
          ? get(placeholders._value_, "totalPrice.currency.name", "")
          : "";
      // eslint-disable-next-line max-len
      return `Total item should be greater than ${placeholders.minItem} and total price should be greater than ${currency}${minTotalRange}`;
    }

    if (
      !icpIsPassMax(
        placeholders._value_,
        placeholders.maxItem,
        placeholders.pricePerItem,
      )
    ) {
      const maxTotalRange = formatCurrency(
        placeholders.maxItem * placeholders.pricePerItem,
        {
          symbol: "",
          format: "%s%v",
          locale: "id",
          nanZero: true,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        },
      );
      const currency =
        get(placeholders._value_, "totalPrice.currency.name", null) !== null
          ? get(placeholders._value_, "totalPrice.currency.name", "")
          : "";
      // eslint-disable-next-line max-len
      return `Total item shouldn't be greater than ${placeholders.maxItem} and total price shouldn't be greater than ${currency}${maxTotalRange}`;
    }

    if (!icpNumberOnly(placeholders._value_)) {
      return "Total item and total price should be a number";
    }

    if (!icpPriceNotZero(placeholders._value_)) {
      return `Total price shouldn't be zero`;
    }

    // if (!icpNameChar(placeholders._value_)) {
    //     return 'Item name may contain only alphabet, number, space, period, and single quote';
    // }

    return "";
  },
});

extend("gadgetServiceCenterCoverage", {
  params: ["isAreaCoverage"],
  validate(value, { isAreaCoverage }) {
    return !!isAreaCoverage;
    // return isAreaCoverage;
  },
  message: " ",
});

extend("gadgetServiceCenterCheck", {
  params: ["isSameLocation"],
  validate(value, { isSameLocation }) {
    // return isSameLocation !== null ? isSameLocation : true;
    return isSameLocation;
  },
});

extend("gadgetChangedProvinceServiceCenter", {
  params: ["province"],
  validate(value, { province }) {
    return province !== null ? province : true;
  },
  message: "{_field_} is required",
});

/* For Life Pricing validations */
extend("validCsvFile", {
  params: ["isValid"],
  validate(_value, { isValid }) {
    return isValid;
  },
  message:
    "The uploaded file is not a valid CSV file. The headers does not match with generated template.",
});

extend("alpha_underscore", {
  message: `{_field_} may only contain alphabetic char & underscores.`,
  validate(value) {
    const regex = /^[A-Za-z_]+$/;

    if (regex.test(value)) return true;
    return false;
  },
});

extend("alpha_underscore_thai", {
  message: `{_field_} may only contain English alphabetic, Thai characters & underscores.`,
  validate(value) {
    // This regex matches English alphabets, Thai characters from U+0E00 to U+0E7F, and underscores
    const regex = /^[A-Za-z\u0E00-\u0E7F_]+$/;

    return regex.test(value);
  },
});

extend("JSON", {
  async validate(value) {
    async function isValidJSON(value) {
      try {
        JSON.parse(value);
        return true;
      } catch (e) {
        return false;
      }
    }
    return await isValidJSON(value);
  },
  message: `JSON is malformed`,
});

extend("totalPercentage", {
  validate(
    _,
    { additionalRevenueForAgent, discountForClient, discountAmount },
  ) {
    const agentValue = parseFloat(additionalRevenueForAgent) || 0;
    const clientValue = parseFloat(discountForClient) || 0;
    const total = agentValue + clientValue;
    return total <= discountAmount;
  },
  params: ["additionalRevenueForAgent", "discountForClient", "discountAmount"],
  message:
    "Additional Revenue for Agent + Discount for Client amount cannot more than {discountAmount}%",
});
